<template>
  <v-select
    ref="select"
    class="brand-select-field"
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    v-bind="mergedAttrs"
  >
    <!-- Only forward slots that actually exist and have content -->
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-select>
</template>

<script setup>
import { computed, useAttrs, useSlots, ref } from 'vue'

// Define props
const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
  modelValue: {
    type: [String, Number, Array, Object],
    default: undefined,
  },
})

// Define emits for v-model support
defineEmits(['update:modelValue'])

// Get attrs and slots
const attrs = useAttrs()
const slots = useSlots()

// Template ref
const select = ref(null)

// Define variants
const VARIANTS = {
  basic: {
    variant: 'outlined',
    bg: 'neutral0',
    color: 'blue400',
    classes: ['brand-select-field--basic', 'rounded-lg'],
  },
}

// Computed common attributes
const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = []
  const common = {
    'hide-details': 'auto',
    menuProps: {
      rounded: 'lg',
      locationY: 'bottom',
      offset: [8, 0],
    },
  }

  return {
    ...common,
    ...variant,
    class: [
      ...classes,
      ...(variant?.classes ?? []),
    ],
  }
})

// Normalize classes to arrays
const normalizeClasses = (classes) => {
  if (!classes) return []
  if (Array.isArray(classes)) return classes
  if (typeof classes === 'string') return classes.split(' ').filter(Boolean)
  if (typeof classes === 'object') {
    return Object.entries(classes)
      .filter(([, value]) => value)
      .map(([key]) => key)
  }
  return []
}

// Merge attributes properly, combining classes instead of overwriting
const mergedAttrs = computed(() => {
  const { class: attrsClass, ...otherAttrs } = attrs
  const { class: commonClass, ...commonOtherAttrs } = commonAttrs.value

  const mergedClasses = [
    ...normalizeClasses(commonClass),
    ...normalizeClasses(attrsClass),
  ]

  return {
    ...commonOtherAttrs,
    ...otherAttrs,
    class: mergedClasses.length > 0 ? mergedClasses : undefined,
  }
})

// Expose methods for template ref access
const focus = () => {
  select.value?.$el.querySelector('input')?.focus()
}

// Expose for parent component access
defineExpose({
  focus,
})

// Set inheritAttrs to false
defineOptions({
  inheritAttrs: false,
})
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>

<style lang="scss" scoped>
.v-select.brand-select-field {
  &.brand-select-field--basic:not(.v-input--disabled) {
    // Use lighter border color matching Vue 2 style
    :deep(.v-field__outline) {
      color: rgba(0, 0, 0, 0.15);
    }

    // Adjust border radius to match Vue 2
    :deep(.v-field) {
      border-radius: 4px !important;
    }

    // Ensure proper field density to match Vue 2 sizing
    :deep(.v-field__input) {
      min-height: 48px;
      padding-top: 14px;
      padding-bottom: 14px;
    }
  }
}
</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .v-select.brand-select-field.brand-select-field--basic:not(.v-input--disabled) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }
}
</style>
